import React from 'react';
import { useState } from 'react';
import {
  AlertTriangle,
  Eye,
  XCircle,
  BookOpen,
  ArrowRight,
  Timer,
  Brain,
  Target,
  CircleAlert
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../../hooks/useTheme';

const MCQInstructions = () => {
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();
  const { interview_id } = useParams();
  const [hasAgreed, setHasAgreed] = useState(false);
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const instructions = [
    { icon: <XCircle className="w-5 h-5" />, text: "There is no Negative Marking" },
    { icon: <AlertTriangle className="w-5 h-5" />, text: "Don't try to sneak out of the tab, you will get 2 warnings, after that test will be submitted automatically" },
    { icon: <Timer className="w-5 h-5" />, text: "The test will automatically submit when the time is up" },
    { icon: <Eye className="w-5 h-5" />, text: "Read each question carefully before answering" },
    { icon: <BookOpen className="w-5 h-5" />, text: "No external help or reference materials are allowed during the test" }
  ];

  const features = [
    {
      icon: <Brain className="w-8 h-8 text-pink-500 mx-auto mb-2" />,
      title: "Test Your Knowledge",
      description: "Challenge yourself with carefully curated questions"
    },
    {
      icon: <Target className="w-8 h-8 text-pink-500 mx-auto mb-2" />,
      title: "Clear Objectives",
      description: "Each question is designed to assess specific skills"
    },
  ];

  return (
    <div className={`min-h-screen flex ${!isDark ? 'bg-[#FAF9F6]' : ''}`}>
      {/* Left Section */}
      <div className="w-5/12 p-8 flex flex-col justify-center items-center">
        <div className="max-w-lg w-full space-y-10">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-[#69007f] to-[#fc0987] text-transparent bg-clip-text">
              Ready to Show Your Skills?
            </h1>
            <p className={`text-base leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Take a quick and interactive MCQ assessment to demonstrate your expertise
            </p>
          </div>

          <div className="grid grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className={`p-5 rounded-xl transform hover:scale-105 transition-all duration-300 text-center ${
                  isDark
                    ? 'glass-effect'
                    : 'bg-white border border-gray-200 shadow-sm'
                }`}
              >
                {feature.icon}
                <h3 className={`text-sm font-semibold mb-1 ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>
                  {feature.title}
                </h3>
                <p className={`text-xs leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Section - Instructions */}
      <div className="w-7/12 p-8 flex flex-col justify-center">
        <div className="max-w-2xl w-full mx-auto">
          <div className={`rounded-2xl p-8 shadow-xl ${
            isDark ? 'glass-effect backdrop-blur-lg' : 'bg-white border border-gray-200'
          }`}>
            <h2 className="text-2xl font-bold text-center mb-6 bg-gradient-to-r from-[#69007f] to-[#fc0987] text-transparent bg-clip-text">
              Test Instructions
            </h2>

            <div className="space-y-3">
              {instructions.map((instruction, index) => (
                <div
                  key={index}
                  className={`flex items-start gap-3 p-3 rounded-lg transition-colors duration-150 ${
                    isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="text-pink-500 mt-0.5 flex-shrink-0">
                    {instruction.icon}
                  </div>
                  <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {instruction.text}
                  </p>
                </div>
              ))}
            </div>

            <div className={`mt-5 pt-5 border-t ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
              <label className="flex items-center gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  id="agreement"
                  checked={hasAgreed}
                  onChange={(e) => setHasAgreed(e.target.checked)}
                  className="w-4 h-4 text-pink-500 rounded focus:ring-pink-500 flex-shrink-0 cursor-pointer"
                />
                <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  I have read and agree to follow all the instructions
                </span>
              </label>
            </div>

            <div className="mt-6 flex justify-center">
              <button
                onClick={() => navigate(`/candidate/mcqtest/${interview_id}`)}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                disabled={!hasAgreed}
                className="button group relative px-8 py-3 text-white font-semibold transform transition-all duration-300 hover:scale-105 hover:shadow-lg flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                <span className="flex items-center gap-2 text-white">
                  Proceed to Test
                  <ArrowRight
                    className={`w-5 h-5 transition-transform duration-300 ${
                      isHovered && hasAgreed ? 'translate-x-1' : ''
                    }`}
                  />
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MCQInstructions;
